import { authApi } from '@/services/authen';
import { tokenClaims } from '@/hooks/useParseJwt';
import { usePrivateStore } from '@/store/usePrivateStore';
import { loadAndSaveOrganizationData } from '@/utils/organizationIndexedDBUtils';
import type { ProfileUser } from '@/types/authen';

type ProfileWithChilds = ProfileUser & { childs?: unknown[] };

export type KiemNhiemChild = {
    id: string;
    bpid: string;
    tenBoPhan?: string;
    tenChucVu?: string;
};

export type KiemNhiemSwitchResult =
    | {
        ok: true;
        profile: ProfileUser;
        loginResponse: Record<string, unknown>;
        email: string;
        tenantID: string;
    }
    | { ok: false };

export type KiemNhiemLoginResult = {
    profile: ProfileUser;
    switched: boolean;
    loginResponse?: Record<string, unknown>;
    email?: string;
    tenantID?: string;
    kiemNhiemId?: string;
};

function getProfileId(profile: ProfileWithChilds | null | undefined): string {
    return String(profile?.id ?? '').trim();
}

function getProfileBoPhanId(profile: ProfileWithChilds | null | undefined): string {
    return String(profile?.boPhanId ?? '').trim();
}

function normalizeKiemNhiemChild(raw: Record<string, unknown>): KiemNhiemChild | null {
    const id = String(raw.id ?? raw.ID ?? '').trim();
    if (!id) return null;

    return {
        id,
        bpid: String(raw.bpid ?? raw.BPID ?? raw.boPhanId ?? raw.BoPhanId ?? '').trim(),
        tenBoPhan: String(raw.tenBoPhan ?? raw.TenBoPhan ?? '').trim() || undefined,
        tenChucVu: String(raw.tenChucVu ?? raw.TenChucVu ?? '').trim() || undefined,
    };
}

export function getKiemNhiemChilds(profile: ProfileWithChilds | null | undefined): KiemNhiemChild[] {
    const rawChilds = ((profile as { childs?: unknown[] })?.childs ?? []) as unknown[];
    return rawChilds
        .map((item) => normalizeKiemNhiemChild((item ?? {}) as Record<string, unknown>))
        .filter((item): item is KiemNhiemChild => Boolean(item));
}

/** Mặc định: child có bpid trùng profile.boPhanId. */
export function resolveDefaultKiemNhiemId(
    profile: ProfileWithChilds | null | undefined,
    storedId?: string,
): string | undefined {
    const childs = getKiemNhiemChilds(profile);
    if (childs.length === 0) return undefined;
    if (childs.length === 1) return childs[0].id;

    if (storedId && childs.some((child) => child.id === storedId)) {
        return storedId;
    }

    const profileBoPhanId = getProfileBoPhanId(profile);
    const profileId = getProfileId(profile);

    if (profileBoPhanId) {
        const byBoPhan = childs.filter((child) => child.bpid === profileBoPhanId);
        if (byBoPhan.length === 1) return byBoPhan[0].id;
        if (byBoPhan.length > 1) {
            return byBoPhan.find((child) => child.id === profileId)?.id ?? byBoPhan[0].id;
        }
    }

    if (profileId && childs.some((child) => child.id === profileId)) {
        return profileId;
    }

    return childs[0]?.id;
}

/** Lưu kiêm nhiệm đang chọn vào appHeader. */
export function syncKiemNhiemBpid(
    profile: ProfileWithChilds | null | undefined,
    storedId?: string,
): string | undefined {
    const targetId = resolveDefaultKiemNhiemId(profile, storedId);
    if (targetId) {
        usePrivateStore.getState().setAppHeader({ kiemNhiemBpid: targetId });
    }
    return targetId;
}

/** Gọi API switch sang profile kiêm nhiệm. */
export async function switchToKiemNhiem(employeeId: string): Promise<KiemNhiemSwitchResult> {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const response: any = await authApi.requestSwitchService({
        email: employeeId,
        password: '',
        kiemNhiem: true,
    });

    if (!response?.success) return { ok: false };

    const claims = tokenClaims(response.accessToken);
    if (!claims?.id) return { ok: false };

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const responseProfile: any = await authApi.requestUserProfile(claims.id);
    if (!responseProfile?.success) return { ok: false };

    await loadAndSaveOrganizationData(response.tenantID, 1);

    return {
        ok: true,
        profile: responseProfile.data as ProfileUser,
        loginResponse: response,
        email: String(claims.email || claims.name || ''),
        tenantID: String(response.tenantID || ''),
    };
}

/** Sau login: đồng bộ kiêm nhiệm theo boPhanId, switch nếu profile chưa khớp. */
export async function applyKiemNhiemOnLogin(
    profile: ProfileWithChilds,
): Promise<KiemNhiemLoginResult> {
    if (getKiemNhiemChilds(profile).length <= 1) {
        return { profile, switched: false };
    }

    const targetId = syncKiemNhiemBpid(profile);
    if (!targetId) {
        return { profile, switched: false };
    }

    if (getProfileId(profile) === targetId) {
        return { profile, switched: false, kiemNhiemId: targetId };
    }

    const switched = await switchToKiemNhiem(targetId);
    if (!switched.ok) {
        return { profile, switched: false, kiemNhiemId: targetId };
    }

    usePrivateStore.getState().clearTreeSelectDepartment();

    return {
        profile: switched.profile,
        switched: true,
        loginResponse: switched.loginResponse,
        email: switched.email,
        tenantID: switched.tenantID,
        kiemNhiemId: targetId,
    };
}
